import React from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  bloodGroup: Yup.string().required('Blood group is required'),
  allergies: Yup.array().of(Yup.string()),
  chronicConditions: Yup.array().of(Yup.string()),
  currentMedications: Yup.array().of(Yup.string()),
  primaryPhysician: Yup.string()
});

const optionalValidationSchema = Yup.object().shape({
  bloodGroup: Yup.string(),
  allergies: Yup.array().of(Yup.string()),
  chronicConditions: Yup.array().of(Yup.string()),
  currentMedications: Yup.array().of(Yup.string()),
  primaryPhysician: Yup.string()
});

const MedicalInformation = ({ initialData, onComplete, onBack, optional }) => {
  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Medical Information</h2>
      <Formik
        initialValues={{
				  bloodGroup: initialData.bloodGroup || '',
				  allergies: initialData.allergies || [''],
				  chronicConditions: initialData.chronicConditions || [''],
				  currentMedications: initialData.currentMedications || [''],
				  primaryPhysician: initialData.primaryPhysician || ''
        }}
        validationSchema={
					!optional ? validationSchema : optionalValidationSchema
				}
        onSubmit={onComplete}
      >
        {({ values, errors, touched, isValid, dirty }) => (
          <Form className={css(styles.form)}>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='bloodGroup' className={css(styles.label)}>
                Blood Group
              </label>
              <Field
                name='bloodGroup'
                as='select'
                className={css(styles.input)}
              >
                <option value=''>Select Blood Group</option>
                <option value='A+'>A+</option>
                <option value='A-'>A-</option>
                <option value='B+'>B+</option>
                <option value='B-'>B-</option>
                <option value='AB+'>AB+</option>
                <option value='AB-'>AB-</option>
                <option value='O+'>O+</option>
                <option value='O-'>O-</option>
              </Field>
              {errors.bloodGroup && touched.bloodGroup && (
                <div className={css(styles.error)}>{errors.bloodGroup}</div>
              )}
            </div>

            <FieldArray name='allergies'>
              {(arrayHelpers) => (
                <div className={css(styles.fieldGroup)}>
                  <label className={css(styles.label)}>Known Allergies</label>
                  {values.allergies.map((allergy, index) => (
                    <div key={index} className={css(styles.arrayField)}>
                      <Field
                        name={`allergies.${index}`}
                        className={css(styles.input)}
                      />
                      <button
                        type='button'
                        onClick={() => arrayHelpers.remove(index)}
                        className={css(styles.removeButton)}
                      >
                        -
                      </button>
                    </div>
                  ))}
                  <button
                    type='button'
                    onClick={() => arrayHelpers.push('')}
                    className={css(styles.addButton)}
                  >
                    Add Allergy
                  </button>
                </div>
              )}
            </FieldArray>

            <FieldArray name='chronicConditions'>
              {(arrayHelpers) => (
                <div className={css(styles.fieldGroup)}>
                  <label className={css(styles.label)}>
                    Chronic Conditions
                  </label>
                  {values.chronicConditions.map((condition, index) => (
                    <div key={index} className={css(styles.arrayField)}>
                      <Field
                        name={`chronicConditions.${index}`}
                        className={css(styles.input)}
                      />
                      <button
                        type='button'
                        onClick={() => arrayHelpers.remove(index)}
                        className={css(styles.removeButton)}
                      >
                        -
                      </button>
                    </div>
                  ))}
                  <button
                    type='button'
                    onClick={() => arrayHelpers.push('')}
                    className={css(styles.addButton)}
                  >
                    Add Condition
                  </button>
                </div>
              )}
            </FieldArray>

            <FieldArray name='currentMedications'>
              {(arrayHelpers) => (
                <div className={css(styles.fieldGroup)}>
                  <label className={css(styles.label)}>
                    Current Medications (Optional)
                  </label>
                  {values.currentMedications.map((medication, index) => (
                    <div key={index} className={css(styles.arrayField)}>
                      <Field
                        name={`currentMedications.${index}`}
                        className={css(styles.input)}
                      />
                      <button
                        type='button'
                        onClick={() => arrayHelpers.remove(index)}
                        className={css(styles.removeButton)}
                      >
                        -
                      </button>
                    </div>
                  ))}
                  <button
                    type='button'
                    onClick={() => arrayHelpers.push('')}
                    className={css(styles.addButton)}
                  >
                    Add Medication
                  </button>
                </div>
              )}
            </FieldArray>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='primaryPhysician' className={css(styles.label)}>
                Primary Physician (Optional)
              </label>
              <Field name='primaryPhysician' className={css(styles.input)} />
            </div>
            <div className={css(styles.buttonContainer)}>
              <motion.button
                type='submit'
                className={css(
								  styles.button,
								  optional && !(isValid && dirty) && styles.disabledButton
                )}
                whileHover={optional && isValid && dirty ? { scale: 1.05 } : {}}
                whileTap={optional && isValid && dirty ? { scale: 0.95 } : {}}
                disabled={optional ? !(isValid && dirty) : false}
              >
                {!optional ? 'Next' : 'Update Profile'}
              </motion.button>
              {!optional
                ? (
                  <motion.button
                    type='button'
                    className={css(styles.button, styles.backButton)}
                    onClick={onBack}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Back
                  </motion.button>
                  )
                : null}
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    marginBottom: '0.5rem',
    fontSize: '1rem',
    color: '#4a5568'
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  error: {
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  arrayField: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.5rem'
  },
  removeButton: {
    backgroundColor: '#e53e3e',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    fontSize: '1rem',
    cursor: 'pointer',
    marginLeft: '0.5rem'
  },
  addButton: {
    backgroundColor: '#48bb78',
    color: 'white',
    border: 'none',
    padding: '0.25rem 0.5rem',
    borderRadius: '5px',
    fontSize: '0.875rem',
    cursor: 'pointer',
    marginTop: '0.5rem'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    backgroundColor: '#2783F7',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer',
    marginTop: '1rem'
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  }
});

export default MedicalInformation;
